import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cmsApi from '../../services/cmsApi';
import { getImageUrl, FALLBACK_IMAGE_SVG } from '../../utils/getImageUrl';

const UpcomingEvents = () => {
  const [eventsData, setEventsData] = useState({
    mainTitle: '',
    subtitle: '',
    cardButtonText: 'SEE ALL ABOUT',
    mainCtaText: 'SEE ALL EVENTS',
    mainCtaLink: '/events',
    events: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch events content from CMS and Events API (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchEventsContent = async () => {
      try {
        setLoading(true);
        setError('');
        // Fetch CMS settings - try both 'upcoming-events' and 'events' types
        let cmsResult = await cmsApi.getHomepageComponentByType('upcoming-events');
        if (!cmsResult.success || !cmsResult.components || cmsResult.components.length === 0) {
          // Fallback to 'events' type
          cmsResult = await cmsApi.getHomepageComponentByType('events');
        }
        
        let cmsSettings = {};
        if (cmsResult.success && cmsResult.components && cmsResult.components.length > 0) {
          const component = cmsResult.components[0];
          const settings = component.settings || {};
          // Map CMS fields to frontend variables
          // mainTitle -> component.title (Campus Highlights Title)
          // subtitle -> component.subtitle (Upcoming Events Subtitle)
          // cardButtonText -> settings.cardButtonText (Individual Event Button Text)
          // mainCtaText -> component.ctaText (See All Events CTA Text)
          // mainCtaLink -> component.ctaLink (See All Events Link)
          cmsSettings = {
            mainTitle: component.title || '',
            subtitle: component.subtitle || '',
            cardButtonText: settings.cardButtonText || 'SEE ALL ABOUT',
            mainCtaText: component.ctaText || 'SEE ALL EVENTS',
            mainCtaLink: component.ctaLink || '/events'
          };
        }

        const mapEvent = (event) => ({
          id: event.id,
          slug: event.slug || event.id,
          title: event.title,
          description: event.description || '',
          image: event.image || '',
          date: event.date || '',
          dateISO: event.dateISO || ''
        });

        const upcomingResult = await cmsApi.getEvents({ upcoming: 'true', limit: 3 });
        let events = (upcomingResult.success && upcomingResult.events) ? upcomingResult.events : [];

        if (events.length < 3) {
          const allResult = await cmsApi.getEvents({ limit: 3 });
          if (allResult.success && allResult.events) {
            const upcomingIds = new Set(events.map(e => e.id));
            const extras = allResult.events.filter(e => !upcomingIds.has(e.id));
            events = [...events, ...extras].slice(0, 3);
          }
        }

        setEventsData({ ...cmsSettings, events: events.map(mapEvent) });
        if (!events.length) {
          setError('No upcoming events available right now.');
        }
      } catch (error) {
        setError('Unable to load events right now.');
        setEventsData({
          mainTitle: '',
          subtitle: '',
          cardButtonText: 'SEE ALL ABOUT',
          mainCtaText: 'SEE ALL EVENTS',
          mainCtaLink: '/events',
          events: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEventsContent();
  }, []);

  const events = eventsData.events;

  return (
    <section className="bg-gray-100 py-16 px-8 text-center min-[1024px]:max-[1535px]:!py-12 min-[1024px]:max-[1535px]:!px-6">
        <h2 className="text-4xl font-bold text-center text-custom-pink mb-6 min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-4">{eventsData.mainTitle}</h2>
        <span className="inline-block border-solid  border-b-4 border-gold mb-4 w-28 h-2 min-[1024px]:max-[1535px]:!mb-3 min-[1024px]:max-[1535px]:!w-24 min-[1024px]:max-[1535px]:!border-b-2"></span>

      <div className="container mx-auto shadow-lg p-8 rounded-lg md:block hidden min-[1024px]:max-[1535px]:!p-6">
        <h2 className="text-3xl font-bold text-left text-custom-pink mb-12 min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-8">{eventsData.subtitle}</h2>
        {error && <p className="text-sm text-red-500 mb-4 text-left">{error}</p>}
        {loading && <p className="text-sm text-gray-500 mb-4 text-left">Loading events...</p>}

        <div className="flex flex-col lg:flex-row flex-nowrap justify-center gap-8 min-[1024px]:max-[1535px]:!gap-5">
          {events.map((event, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden w-full lg:w-[calc(33%-1rem)] relative flex flex-col"
            >
              <div className="relative overflow-hidden">
                <img
                  src={getImageUrl(event.image)}
                  alt={event.title}
                  className="w-full h-56 object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE_SVG;
                  }}
                />
                <p className="absolute top-0 left-0 bg-custom-pink text-white py-2 px-4 font-bold text-sm rounded-md shadow-md">
                  {event.date}
                </p>
              </div>
              <div className="flex-grow p-8 flex text-left flex-col justify-between min-[1024px]:max-[1535px]:!p-5">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 min-[1024px]:max-[1535px]:!text-lg min-[1024px]:max-[1535px]:!mb-3">{event.title}</h3>
                  <p className="text-gray-700 mb-6 min-[1024px]:max-[1535px]:!text-sm min-[1024px]:max-[1535px]:!mb-4">{event.description}</p>
                </div>
                <Link
                  to={`/events/${event.slug || event.id}`}
                  className="bg-custom-pink text-white w-full py-2 rounded-md border border-transparent hover:bg-transparent hover:text-custom-pink hover:border-custom-pink transition duration-300 mt-auto text-center hover:scale-105 hover:shadow-lg min-[1024px]:max-[1535px]:!text-sm min-[1024px]:max-[1535px]:!py-1.5"
                >
                  {eventsData.cardButtonText}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* See All Button */}
        <div className="text-center mt-12 min-[1024px]:max-[1535px]:!mt-8">
          <Link
            to={eventsData.mainCtaLink}
            className="bg-none text-custom-pink border border-custom-pink px-10 py-5 rounded-full hover:bg-custom-pink hover:text-white transition duration-300 inline-block min-[1024px]:max-[1535px]:!px-6 min-[1024px]:max-[1535px]:!py-3 min-[1024px]:max-[1535px]:!text-sm"
          >
            {eventsData.mainCtaText}
          </Link>
        </div>
      </div>

      {/* Mobile version without container */}
      <div className="md:hidden px-4">
        <h2 className="text-2xl font-bold text-left text-custom-pink mb-8">{eventsData.subtitle}</h2>
        {error && <p className="text-sm text-red-500 mb-4 text-left">{error}</p>}
        {loading && <p className="text-sm text-gray-500 mb-4 text-left">Loading events...</p>}

        <div className="flex flex-col gap-6">
          {events.map((event, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden w-full relative flex flex-col"
            >
              <div className="relative">
                <img
                  src={getImageUrl(event.image)}
                  alt={event.title}
                  className="w-full h-56 object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE_SVG;
                  }}
                />
                <p className="absolute top-0 left-0 bg-custom-pink text-white py-2 px-4 font-bold text-sm rounded-md shadow-md">
                  {event.date}
                </p>
              </div>
              <div className="flex-grow p-6 flex text-left flex-col justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{event.title}</h3>
                  <p className="text-gray-700 mb-6">{event.description}</p>
                </div>
                <Link
                  to={`/events/${event.slug || event.id}`}
                  className="bg-custom-pink text-white w-full py-2 rounded-md border border-transparent hover:bg-transparent hover:text-custom-pink hover:border-custom-pink transition duration-300 mt-auto text-center hover:scale-105 hover:shadow-lg"
                >
                  {eventsData.cardButtonText}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* See All Button */}
        <div className="text-center mt-8">
          <Link
            to={eventsData.mainCtaLink}
            className="bg-none text-custom-pink border border-custom-pink px-8 py-4 rounded-full hover:bg-custom-pink hover:text-white transition duration-300 inline-block text-sm"
          >
            {eventsData.mainCtaText}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
